import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { themesApi } from '../../../services/api';
import StuiSelect from '../../../components/common/StuiSelect';

const errText = (e) => {
  const d = e?.response?.data;
  if (typeof d === 'string') return d;
  return d?.error || d?.message || e?.message || 'Request failed';
};

/** STUI popup skeleton: overlay → modal → blue header → body → footer. */
export const ThemeModal = ({ title, onClose, children, footer, size = 'md' }) => (
  <div className="stui-modal-overlay">
    <div className={`stui-modal stui-modal-${size}`}>
      <div className="stui-modal-header">
        <h3>{title}</h3>
        <button type="button" className="stui-modal-close" onClick={onClose}><X size={20} /></button>
      </div>
      <div className="stui-modal-body">{children}</div>
      <div className="stui-modal-footer">{footer}</div>
    </div>
  </div>
);

const ErrorLine = ({ error }) => (error ? <div className="stui-label-note stui-text-error">{error}</div> : null);

/** Delete: usage first; if anything points at the theme a replacement is required. */
export const DeleteThemeDialog = ({ theme, themes, uid, onDone, onClose }) => {
  const [usage, setUsage] = useState(null);
  const [replaceWith, setReplaceWith] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    themesApi.usage(theme.themeKey, uid)
      .then(r => setUsage(r.data || { users: 0, defaults: 0 }))
      .catch(e => setError(errText(e)));
  }, [theme.themeKey, uid]);

  const inUse = usage && (usage.users > 0 || usage.defaults > 0);
  const candidates = themes.filter(t => t.themeKey !== theme.themeKey && t.status === 'Active');
  const ready = usage && (!inUse || replaceWith);

  const confirm = async () => {
    setBusy(true); setError(null);
    try {
      await themesApi.remove(theme.themeKey, inUse ? replaceWith : undefined, uid);
      onDone(inUse ? replaceWith : null);
    } catch (e) { setError(errText(e)); setBusy(false); }
  };

  return (
    <ThemeModal
      title="Delete Theme"
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="stui-btn stui-btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="stui-btn stui-btn-danger" onClick={confirm} disabled={!ready || busy}>Delete</button>
        </>
      )}
    >
      <p>Delete <strong>{theme.label}</strong> permanently? This cannot be undone.</p>
      {!usage && !error && <div className="stui-label-note">Checking usage…</div>}
      {inUse && (
        <>
          <div className="stui-label-note">
            {usage.users} user selection{usage.users === 1 ? '' : 's'} and {usage.defaults} default{usage.defaults === 1 ? '' : 's'} use this
            theme. Pick a replacement; they are moved to it before the delete.
          </div>
          <StuiSelect className="stui-field-input" value={replaceWith} onChange={e => setReplaceWith(e.target.value)}>
            <option value="">-- Replacement theme --</option>
            {candidates.map(t => <option key={t.themeKey} value={t.themeKey}>{t.label}</option>)}
          </StuiSelect>
        </>
      )}
      <ErrorLine error={error} />
    </ThemeModal>
  );
};

/** Apply a draft/clone onto an existing theme (the sanctioned way to update a built-in). */
export const ApplyToDialog = ({ theme, themes, uid, onDone, onClose }) => {
  const guess = theme.themeKey.replace(/-(tuned|copy)$/, '');
  const targets = themes.filter(t => t.themeKey !== theme.themeKey);
  const [target, setTarget] = useState(targets.some(t => t.themeKey === guess) ? guess : '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const targetTheme = targets.find(t => t.themeKey === target);

  const confirm = async () => {
    setBusy(true); setError(null);
    try { await themesApi.applyTo(theme.themeKey, target, uid); onDone(target); }
    catch (e) { setError(errText(e)); setBusy(false); }
  };

  return (
    <ThemeModal
      title="Apply To Theme"
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="stui-btn stui-btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="stui-btn stui-btn-primary" onClick={confirm} disabled={!target || busy}>Apply and delete draft</button>
        </>
      )}
    >
      <p>
        Copy the colors of <strong>{theme.label}</strong> into the theme below, keeping its key and label, then delete
        <strong> {theme.label}</strong>. Everyone using the target follows automatically.
      </p>
      <StuiSelect className="stui-field-input" value={target} onChange={e => setTarget(e.target.value)}>
        <option value="">-- Target theme --</option>
        {targets.map(t => <option key={t.themeKey} value={t.themeKey}>{t.label}{t.isSystem ? ' (built-in)' : ''}</option>)}
      </StuiSelect>
      {targetTheme?.isSystem && (
        <div className="stui-label-note">The target is a built-in theme; its colors will be replaced for everyone.</div>
      )}
      <ErrorLine error={error} />
    </ThemeModal>
  );
};
